import { type AppLocale, isTargetAppLocale, SOURCE_LOCALE } from '@govinity/shared-config/locales';

import { env } from '@/env.mjs';

/**
 * A development affordance for exercising translations without touching browser language settings.
 * The cookie is the source of truth: the Next server reads it when picking message catalogs, and
 * the API client forwards it as `LOCALE_OVERRIDE_HEADER` so dynamic content matches the shell.
 * Both sides ignore it outside the environments below, so it cannot leak into staging or prod.
 */
export const LOCALE_OVERRIDE_COOKIE = 'govinity-locale-override';

export const LOCALE_OVERRIDE_HEADER = 'x-locale-override';

export const isLocaleOverrideEnabled = ['development', 'integration'].includes(
  env.NEXT_PUBLIC_APP_ENV
);

export const toOverrideLocale = (value: string | undefined): AppLocale | undefined => {
  if (!isLocaleOverrideEnabled) return undefined;
  if (value === SOURCE_LOCALE) return SOURCE_LOCALE;
  return isTargetAppLocale(value) ? value : undefined;
};

export const readLocaleOverride = (): AppLocale | undefined => {
  if (!isLocaleOverrideEnabled || typeof document === 'undefined') return undefined;

  const match = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${LOCALE_OVERRIDE_COOKIE}=`));

  return toOverrideLocale(match?.split('=')[1]);
};

/**
 * Writes the override and reloads, rather than refreshing the router, so the server components,
 * the React Query caches and every in-flight signed URL are all rebuilt against the new locale.
 */
export const applyLocaleOverride = (locale: AppLocale | null) => {
  document.cookie = locale
    ? `${LOCALE_OVERRIDE_COOKIE}=${locale}; path=/; max-age=31536000; samesite=lax`
    : `${LOCALE_OVERRIDE_COOKIE}=; path=/; max-age=0; samesite=lax`;

  window.location.reload();
};
